/**
 * Where a team's next scheduled run starts, as the api plans it now: a sealed
 * adapter, a fresh adapter, or why the plan cannot say (TRA-1662). A list
 * of held-out tests mostly replaced starts a fresh adapter, and this is where
 * staff see that before the run.
 *
 * The plan is the api's (TrainingScheduleResponse `next_start`), so it says
 * what is saved and built. Neither a draft of the schedule nor an unsaved
 * tick of a held-out test changes it. The held-out dialog cannot preview a
 * new list at all: it edits test files, and the task ids a plan compares
 * exist only after a dataset build.
 */

import type { TrainingScheduleResponse } from '@/api/generated/model'
import { Skeleton } from '@/components/ui/skeleton'

import { nextRunLine } from './schedule'

const EYEBROW = 'font-mono text-[10px] uppercase tracking-wider text-muted-foreground'

/** The value of a "Starts from" row. */
export function PlannedStart({ data }: { data: TrainingScheduleResponse }): React.JSX.Element {
  const { next_start: start, start_problem: problem } = data
  if (start === null) {
    return problem === null ? (
      <p className="text-sm text-muted-foreground">This team has no scheduled run.</p>
    ) : (
      <p className="text-sm text-destructive">
        The next run has no plan yet. The api says: {problem}.
      </p>
    )
  }
  return (
    <div className="space-y-1">
      {start.checkpoint === null ? (
        <p className="text-sm text-foreground">A fresh adapter</p>
      ) : (
        <p className="break-all font-mono text-xs text-foreground">{start.checkpoint}</p>
      )}
      <p className="text-xs text-muted-foreground">{start.why}</p>
    </div>
  )
}

/**
 * The "Held-out tests" dialog's view of the next run. Reading it must never
 * stand in the way of the tests, so it loads and fails on its own.
 */
export function TodaysPlan({
  data,
  isError,
}: {
  data: TrainingScheduleResponse | undefined
  isError: boolean
}): React.JSX.Element {
  if (isError) {
    return <p className="text-xs text-destructive">The plan for the next run did not load.</p>
  }
  if (data === undefined) return <Skeleton className="h-24 w-full" />
  if (data.schedule === null) return <PlannedStart data={data} />
  return (
    <div className="space-y-2">
      <dl className="space-y-3 border border-border p-3">
        <div className="space-y-1">
          <dt className={EYEBROW}>Next run</dt>
          <dd className="text-sm text-foreground">{nextRunLine(data.schedule)}</dd>
        </div>
        <div className="space-y-1">
          <dt className={EYEBROW}>Starts from</dt>
          <dd>
            <PlannedStart data={data} />
          </dd>
        </div>
      </dl>
      {data.next_start !== null && (
        <p className="text-xs text-muted-foreground">
          This is today&apos;s plan. A new list changes it only after a training dataset is built
          from that list, and a list mostly replaced can start a fresh adapter.
        </p>
      )}
    </div>
  )
}
